import { FormattedMessage, useIntl } from 'react-intl';
import { FormTextField, LoadingButton } from 'components/common';
import { FcaTextfield } from 'pages/SignupPage/NewAuthFormComponents/FcaTextfield';
import { useFormContext, useWatch } from 'react-hook-form';
import { SignupOrganisationFormFields } from '../signupOrganisationForm.types';
import { AddressFields } from '../AddressFields';
import { brokerOrgFormRoot, formPath, orgField } from '../formFieldPaths';

const textIn = {
  type: 'text' as const,
  inputProps: { sx: { padding: '6px !important' } as const, className: 'text-sm !font-normal' as const },
};

type ClubFormProps = {
  isFcaLoading: boolean;
  onFcaFind: () => void;
  disabled: boolean;
  showFieldsAfterFca: boolean;
  titleId?: string;
};

export function ClubForm({
  isFcaLoading,
  onFcaFind,
  disabled,
  showFieldsAfterFca,
  titleId = 'auth.signup.linkOrganisationDA.title',
}: ClubFormProps) {
  const intl = useIntl();
  const { control } = useFormContext<SignupOrganisationFormFields>();
  const base = brokerOrgFormRoot.mortgageClub;
  const clubBackendId = useWatch({ name: formPath(base, orgField.backendId), control });
  const locked = Boolean(clubBackendId);
  const required = intl.formatMessage({ id: 'common.error.requiredField' });
  const showRest = showFieldsAfterFca || locked;

  return (
    <div className='grid w-full max-w-4xl grid-cols-1 gap-4 border-t border-gray-200 pt-6 md:grid-cols-2'>
      <h3 className='md:col-span-2 text-base font-semibold'>
        <FormattedMessage id={titleId} />
      </h3>
      <div className='flex flex-col gap-2 sm:flex-row sm:items-end md:col-span-2'>
        <div className='min-w-0 grow'>
          <FcaTextfield name={formPath(base, orgField.financialConductAuthorityNumber)} />
        </div>
        {!isFcaLoading && !showFieldsAfterFca && (
          <LoadingButton
            isLoading={isFcaLoading}
            buttonProps={{
              type: 'button',
              variant: 'contained',
              className: 'h-9 shrink-0',
              onClick: onFcaFind,
              disabled: disabled || locked,
            }}
          >
            <FormattedMessage id='cta.continue' />
          </LoadingButton>
        )}
      </div>
      {showRest && (
        <>
          <div className='md:col-span-2'>
            <FormattedMessage id='auth.signup.companyName' />
            <FormTextField
              name={formPath(base, orgField.name)}
              rules={{ required: { value: true, message: required } }}
              disabled={disabled || locked}
              textFieldProps={{
                placeholder: intl.formatMessage({ id: 'placeholders.companyDetails.name' }),
                ...textIn,
              }}
            />
          </div>
          <div className='md:col-span-2'>
            <div className='mb-1 text-sm font-medium'>Mortgage club address</div>
            <AddressFields group={base} disabled={Boolean(disabled || locked)} />
          </div>
        </>
      )}
    </div>
  );
}
